import { create } from "zustand";
import { getServerTime } from "services/api";

export interface ITimer {
  minutes: number;
  seconds: number;
  end: boolean;
  expiresAt: string | null;
  serverOffset: number;
}

interface ITimerData extends ITimer {
  setMinutes(minutes: number): void;
  setSeconds(seconds: number): void;
  setTimer(seconds: number): void;
  setExpiresAt(expiresAt: string): void;
  syncServerTime(): Promise<void>;
  decreaseTimer(): void;
  calculateRemaining(): number;
}

export const initialState: ITimer = {
  minutes: 0,
  seconds: 0,
  end: false,
  expiresAt: null,
  serverOffset: 0,
};

const useTimerData = create<ITimerData>((set, get) => ({
  ...initialState,
  setMinutes: (minutes: number) => set({ minutes }),
  setSeconds: (seconds: number) => set({ seconds }),
  
  // Sync with server time to handle timezone differences
  syncServerTime: async () => {
    try {
      const { timestamp } = await getServerTime();
      const clientTime = Date.now();
      const offset = timestamp - clientTime;
      set({ serverOffset: offset });
    } catch (error) {
      console.error('Failed to sync server time:', error);
    }
  },

  // Set expiration time from server (UTC)
  setExpiresAt: (expiresAt: string) => {
    set({ expiresAt, end: false });
    // Calculate initial remaining time
    const remaining = get().calculateRemaining();
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    set({ minutes, seconds });
  },

  // Calculate remaining seconds based on server time
  calculateRemaining: () => {
    const { expiresAt, serverOffset } = get();
    if (!expiresAt) return 0;
    
    const serverNow = Date.now() + serverOffset;
    const expiresTime = new Date(expiresAt).getTime();
    const remaining = Math.max(0, Math.floor((expiresTime - serverNow) / 1000));
    return remaining;
  },

  setTimer: (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    set({ minutes, seconds: remainingSeconds, end: false });
  },

  decreaseTimer: () => {
    const { expiresAt } = get();
    
    // If we have expiresAt, recalculate based on real time
    if (expiresAt) {
      const remaining = get().calculateRemaining();
      if (remaining <= 0) {
        set({ minutes: 0, seconds: 0, end: true });
        return;
      }
      const minutes = Math.floor(remaining / 60);
      const seconds = remaining % 60;
      set({ minutes, seconds });
      return;
    }

    // Fallback to simple countdown
    const { minutes, seconds } = get();
    if (minutes === 0 && seconds === 0) {
      set({ end: true });
      return;
    }

    if (seconds > 0) {
      set({ seconds: seconds - 1 });
    } else if (minutes > 0) {
      set({ minutes: minutes - 1, seconds: 59 });
    }
  },
}));

export default useTimerData;